import React, { useState, useEffect } from "react"
import {
  Box,
  AccordionDetails,
  Button,
  IconButton
} from "@mui/material"
import { 
  ArrowForward, 
  ExpandMore, 
  Edit, 
  CalendarMonth, 
  Work, 
  AccessTime, 
  LocationCity, 
  EmojiEvents,
  WorkHistory,
  ViewWeek
} from "@mui/icons-material"
import { useHistory } from 'react-router'
import { EditPostMode, EditedJob, SevenBinaryArray } from "../types"
import { TopBarRedesigned, AdditionalActionsIconType } from "components/TopBarRedesigned"
import DentistSideMenu from "components/DentistSideMenu"
import { exitReact, formatFullLocation } from "utils"
import dayjs from "dayjs"
import { 
  MainContainer, 
  ContentContainer, 
  PageTitle, 
  LocationPaper, 
  LocationTitle, 
  LocationText, 
  StyledAccordion, 
  StyledAccordionSummary, 
  AccordionTitleContainer, 
  AccordionTitle, 
  DetailsGrid, 
  DetailItemContainer, 
  IconContainer, 
  DetailLabelText, 
  DetailValueText, 
  DetailContentText
} from "./OverviewStep.styles"
import { Days } from "../config"
import { PublicUrlDisplay } from "../../JobBoard/PublicUrlDisplay"
import { usePersistedState } from "hooks"
import { entities } from "reduxStore"
import { Checkbox } from "@mui/material"
import { Typography, Paper } from "@mui/material"

export interface OverviewStepProps {
  job: EditedJob
  onEdit: () => void
  onClose: () => void
  onConfirm: () => void
  isSubmitting?: boolean
}

const OverviewStep: React.FC<OverviewStepProps> = ({
  job,
  onEdit,
  onClose,
  onConfirm,
  isSubmitting = false
}) => {
  const history = useHistory();
  const [persistedJob, saveJob] = usePersistedState({
    entityName: entities.editedJobPost,
    initialValue: job
  }) as [EditedJob, (j: EditedJob) => void]
  const featureOnPost = !!(persistedJob as any)?.featureOnPost
  const [expandedPanels, setExpandedPanels] = useState<{[key: string]: boolean}>({
    jobDetails: true,
    jobTitle: true,
    jobDescription: true
  });
  // T2-6758: hamburger + side menu
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels({ ...expandedPanels, [panel]: isExpanded });
  };
  
  const renderAvailabilityDays = () => {
    // Handle case where availability is undefined or empty
    if (!job.availability || !Array.isArray(job.availability) || job.availability.length < 1) {
      return 'Not specified';
    }
    
    try {
      if (typeof job.availability[0] === 'object' && job.availability[0] !== null) {
        return job.availability
          .filter(day => day && typeof day === 'object' && 'text' in day)
          .map(day => (day as any).text)
          .join(', ') || 'Not specified';
      }
      
      // Handle case where availability is a binary array [0,1,0,1,0,0,0]
      if (typeof job.availability[0] === 'number') {
        const selectedDays = job.availability
          .map((value, index) => value === 1 ? Days[index].text : null)
          .filter(day => day !== null);
        
        return selectedDays.length > 0 ? selectedDays.join(', ') : 'Not specified';
      }
      
      return 'Not specified';
    } catch (error) {
      console.error("Error rendering availability days:", error);
      return 'Not specified';
    }
  };

  return (
    <>
      <TopBarRedesigned
        onBack={() => {
          history.push({ pathname: 'dentistHome' })
        }}
        additionalActionsIconType={AdditionalActionsIconType.menu}
        onAdditionalActionsButtonClick={() => setIsSideMenuOpen(true)}
      />
      <DentistSideMenu isOpen={isSideMenuOpen} onClose={() => setIsSideMenuOpen(false)} />
      <MainContainer>
        <ContentContainer>
          <PageTitle variant="h4">
            Post a Your Job
          </PageTitle>

          <LocationPaper elevation={1}>
            <LocationTitle variant="h6">
              Job Posting for Location
            </LocationTitle>
            <LocationText variant="body1">
              {job.location?.practiceName}
            </LocationText>
            <LocationText variant="body1">
              {job.location ? formatFullLocation(job.location) : ""}
            </LocationText>
          </LocationPaper>
          
          <StyledAccordion 
            expanded={expandedPanels.jobDetails}
            onChange={handleAccordionChange('jobDetails')}
          >
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <AccordionTitleContainer>
                <AccordionTitle variant="h6">Job Details</AccordionTitle>
                <IconButton 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onEdit(); 
                  }}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <Edit sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </AccordionTitleContainer>
            </StyledAccordionSummary>
            <AccordionDetails>
              <DetailsGrid>
                <DetailItemContainer>
                  <IconContainer>
                    <Work sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Role</DetailLabelText>
                    <DetailValueText>{job.position?.text || "Not specified"}</DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <AccessTime sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Contract</DetailLabelText>
                    <DetailValueText>{job.basis?.text || "Not specified"}</DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <CalendarMonth sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Start Date</DetailLabelText>
                    <DetailValueText>
                      {job.startDate ? dayjs(job.startDate).format('MMMM D, YYYY') : "Not specified"}
                      {job.approxStart ? " (approximate)" : ""}
                    </DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <ViewWeek sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Days Needed</DetailLabelText>
                    <DetailValueText>{renderAvailabilityDays()}</DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <WorkHistory sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Minimum Experience</DetailLabelText>
                    <DetailValueText>{job.requirement?.text || "Not specified"}</DetailValueText>
                  </Box>
                </DetailItemContainer>
              </DetailsGrid>
            </AccordionDetails>
          </StyledAccordion>
          
          <StyledAccordion 
            expanded={expandedPanels.jobTitle}
            onChange={handleAccordionChange('jobTitle')}
          >
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <AccordionTitleContainer>
                <AccordionTitle variant="h6">Job Title</AccordionTitle>
                <IconButton 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onEdit(); 
                  }}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <Edit sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </AccordionTitleContainer>
            </StyledAccordionSummary>
            <AccordionDetails>
              <DetailContentText>{job.title || "Not specified"}</DetailContentText>
            </AccordionDetails>
          </StyledAccordion>
          
          <StyledAccordion 
            expanded={expandedPanels.jobDescription}
            onChange={handleAccordionChange('jobDescription')}
          >
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <AccordionTitleContainer>
                <AccordionTitle variant="h6">Job Description</AccordionTitle>
                <IconButton 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onEdit(); 
                  }}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <Edit sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </AccordionTitleContainer>
            </StyledAccordionSummary>
            <AccordionDetails>
              <DetailContentText sx={{ whiteSpace: 'pre-wrap' }}>{job.description || "Not specified"}</DetailContentText>
            </AccordionDetails>
          </StyledAccordion>
          
          {/* Featured Listing confirmation CTA (analogous to Boost on confirmation) */}
          {/* Only show for Ontario locations (province === "ON") */}
          {job.location?.province === "ON" && (
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, mt: 3, borderRadius: 2, 
                border: '1px solid var(--primary-blue)',
                bgcolor: 'rgba(11,69,152,0.06)'
              }}
            >
              <Typography fontWeight={700} color={'var(--primary-blue)'} sx={{ mb: 1 }}>⭐ Feature this listing</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Get more visibility for your Job Board posting. $25 + HST (charged only when you view your first candidate). If all Featured slots are full in your area, we'll automatically add you to the waitlist and feature your listing when a spot opens.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={featureOnPost}
                  onChange={(_, checked) => saveJob({ ...(persistedJob as any), featureOnPost: checked } as any)}
                  sx={{ color: 'var(--primary-blue)', '&.Mui-checked': { color: 'var(--primary-blue)' } }}
                />
                <Typography>Feature this listing immediately after posting</Typography>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Ontario only. You can also enable this later on the Job Details page.
              </Typography>
            </Paper>
          )}
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            mt: 4, 
            mb: 4,
            width: '100%'
          }}>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={onConfirm}
              disabled={isSubmitting}
              sx={{
                bgcolor: "var(--primary-blue)",
                borderRadius: 10,
                py: 1.5,
                textTransform: "uppercase",
                fontWeight: "bold",
                boxShadow: 2,
                '&:hover': {
                  bgcolor: "#002266",
                },
                '&:disabled': {
                  bgcolor: "rgba(11, 69, 152, 0.5)",
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={onEdit}
              sx={{
                color: "var(--primary-blue)",
                borderColor: "var(--primary-blue)",
                textTransform: "none",
                borderRadius: 2,
                py: 1,
                '&:hover': {
                  borderColor: "var(--primary-blue)",
                  backgroundColor: 'rgba(11, 69, 152, 0.04)',
                },
              }}
            >
              Edit Job Details
            </Button>
          </Box>
        </ContentContainer>
      </MainContainer>
    </>
  )
}

export default OverviewStep;
